import { useState } from "react";
import { Type, Heading, Image as ImgIcon, Quote, MessageSquareQuote, Info, MousePointerClick, Minus, Code, List, Youtube, Package, Images, ChevronUp, ChevronDown, Trash2, Plus, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ImageField } from "@/components/admin/MediaPicker";
import RichTextEditor from "@/components/admin/RichTextEditor";

const uid = () => Math.random().toString(36).slice(2, 10);

const BLOCK_TYPES = [
  { type: "text", label: "Text", icon: Type },
  { type: "heading", label: "Heading", icon: Heading },
  { type: "image", label: "Image", icon: ImgIcon },
  { type: "gallery", label: "Gallery", icon: Images },
  { type: "youtube", label: "YouTube / Video", icon: Youtube },
  { type: "quote", label: "Quote", icon: Quote },
  { type: "pullquote", label: "Pull Quote", icon: MessageSquareQuote },
  { type: "callout", label: "Callout", icon: Info },
  { type: "list", label: "List", icon: List },
  { type: "button", label: "Button / CTA", icon: MousePointerClick },
  { type: "affiliate", label: "Affiliate CTA", icon: MousePointerClick },
  { type: "product", label: "Product / Tool", icon: Package },
  { type: "html", label: "HTML Embed", icon: Code },
  { type: "divider", label: "Divider", icon: Minus },
];

function newBlock(type) {
  const base = { id: uid(), type };
  if (type === "text") return { ...base, html: "" };
  if (type === "heading") return { ...base, level: 2, text: "" };
  if (type === "list") return { ...base, style: "unordered", items: [""] };
  if (type === "quote") return { ...base, text: "", cite: "" };
  if (type === "pullquote") return { ...base, text: "" };
  if (type === "callout") return { ...base, text: "", variant: "info" };
  if (type === "image") return { ...base, url: "", alt: "", caption: "", align: "center", link: "" };
  if (type === "gallery") return { ...base, images: [] };
  if (type === "youtube") return { ...base, url: "" };
  if (type === "button" || type === "affiliate") return { ...base, text: "Learn more", url: "", disclosure: "" };
  if (type === "product") return { ...base, name: "", description: "", image: "", url: "" };
  if (type === "html") return { ...base, html: "" };
  return base;
}

const inputCls = "w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand transition-colors text-white";

function AiTextTools({ text, onApply }) {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [action, setAction] = useState("");

  const run = async (act) => {
    setBusy(act); setAction(act);
    try {
      const { data } = await api.post("/admin/ai/action", { action: act, text });
      setResult(data.result);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const actions = [["SHORTER", "Shorter"], ["EXPAND", "Expand"], ["SIMPLIFY", "Simplify"], ["CONVERSATIONAL", "More conversational"], ["REWRITE", "Rewrite"]];

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-mono-label text-brand flex items-center gap-1"><Sparkles size={11} /> AI</span>
        {actions.map(([a, l]) => (
          <button key={a} type="button" onClick={() => run(a)} disabled={!!busy} className="text-[11px] px-2 py-1 rounded border border-ink-600 text-slate-300 hover:border-brand hover:text-brand transition-colors disabled:opacity-50">
            {busy === a ? "..." : l}
          </button>
        ))}
      </div>
      {result != null && (
        <div className="mt-2 rounded-lg border border-brand/40 bg-brand/5 p-3">
          <div className="text-[10px] font-mono-label text-brand mb-1">AI suggestion ({action})</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap max-h-40 overflow-y-auto thin-scroll">{result.replace(/<[^>]+>/g, "")}</div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => { onApply(result.startsWith("<") ? result : `<p>${result}</p>`); setResult(null); toast.success("Applied"); }} className="inline-flex items-center gap-1 text-xs font-bold bg-brand text-brand-dark px-3 py-1 rounded-full"><Check size={12} /> Apply</button>
            <button type="button" onClick={() => setResult(null)} className="inline-flex items-center gap-1 text-xs text-slate-400 px-2 py-1"><X size={12} /> Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BlockCard({ block, onChange, onDelete, onMove, first, last }) {
  const set = (patch) => onChange({ ...block, ...patch });
  const meta = BLOCK_TYPES.find((b) => b.type === block.type) || { label: block.type, icon: Type };

  return (
    <div className="rounded-xl border border-ink-600 bg-ink-900 p-4" data-testid={`block-${block.type}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-mono-label text-brand flex items-center gap-1.5"><meta.icon size={13} /> {meta.label}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={first} className="p-1 text-slate-500 hover:text-white disabled:opacity-30"><ChevronUp size={15} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={last} className="p-1 text-slate-500 hover:text-white disabled:opacity-30"><ChevronDown size={15} /></button>
          <button type="button" onClick={onDelete} className="p-1 text-slate-500 hover:text-red-400"><Trash2 size={15} /></button>
        </div>
      </div>

      {block.type === "text" && (
        <>
          <RichTextEditor value={block.html} onChange={(html) => set({ html })} />
          <AiTextTools text={block.html} onApply={(html) => set({ html })} />
        </>
      )}

      {block.type === "heading" && (
        <div className="flex gap-2">
          <select value={block.level} onChange={(e) => set({ level: Number(e.target.value) })} className="bg-ink-800 border border-ink-600 rounded-lg px-2 text-sm text-white outline-none">
            <option value={2}>H2</option><option value={3}>H3</option>
          </select>
          <input value={block.text} onChange={(e) => set({ text: e.target.value })} className={inputCls} placeholder="Heading text" />
        </div>
      )}

      {block.type === "list" && (
        <div>
          <select value={block.style} onChange={(e) => set({ style: e.target.value })} className="bg-ink-800 border border-ink-600 rounded-lg px-2 py-1.5 text-sm text-white outline-none mb-2">
            <option value="unordered">Bulleted</option><option value="ordered">Numbered</option>
          </select>
          {(block.items || []).map((it, i) => (
            <div key={i} className="flex gap-2 mb-2 items-start">
              <div className="flex-1"><RichTextEditor value={it} compact placeholder={`Item ${i + 1}. Highlight text to format it.`} onChange={(html) => { const items = [...block.items]; items[i] = html; set({ items }); }} /></div>
              <button type="button" onClick={() => set({ items: block.items.filter((_, x) => x !== i) })} className="p-1.5 mt-1 text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => set({ items: [...(block.items || []), ""] })} className="text-xs text-brand font-semibold mt-1">+ Add item</button>
        </div>
      )}

      {(block.type === "quote" || block.type === "pullquote") && (
        <>
          <textarea value={block.text} onChange={(e) => set({ text: e.target.value })} rows={2} className={inputCls} placeholder="Quote text" />
          {block.type === "quote" && <input value={block.cite || ""} onChange={(e) => set({ cite: e.target.value })} className={`${inputCls} mt-2`} placeholder="Attribution (optional)" />}
        </>
      )}

      {block.type === "callout" && <textarea value={block.text} onChange={(e) => set({ text: e.target.value })} rows={2} className={inputCls} placeholder="Callout text" />}

      {block.type === "image" && (
        <div className="space-y-2">
          <ImageField value={block.url} onChange={(url) => set({ url })} label="" />
          <div className="grid grid-cols-2 gap-2">
            <input value={block.alt || ""} onChange={(e) => set({ alt: e.target.value })} className={inputCls} placeholder="Alt text" />
            <input value={block.caption || ""} onChange={(e) => set({ caption: e.target.value })} className={inputCls} placeholder="Caption" />
            <input value={block.link || ""} onChange={(e) => set({ link: e.target.value })} className={inputCls} placeholder="Link (optional)" />
            <select value={block.align || "center"} onChange={(e) => set({ align: e.target.value })} className={inputCls}><option value="center">Center</option><option value="left">Left</option></select>
          </div>
        </div>
      )}

      {block.type === "gallery" && (
        <div>
          {(block.images || []).map((im, i) => (
            <div key={i} className="flex gap-2 items-center mb-2">
              <input value={im.url || ""} onChange={(e) => { const images = [...block.images]; images[i] = { ...images[i], url: e.target.value }; set({ images }); }} className={inputCls} placeholder="Image URL" />
              <button type="button" onClick={() => set({ images: block.images.filter((_, x) => x !== i) })} className="p-1 text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => set({ images: [...(block.images || []), { url: "", alt: "" }] })} className="text-xs text-brand font-semibold">+ Add image</button>
        </div>
      )}

      {block.type === "youtube" && <input value={block.url} onChange={(e) => set({ url: e.target.value })} className={inputCls} placeholder="YouTube URL or video URL" />}

      {(block.type === "button" || block.type === "affiliate") && (
        <div className="grid grid-cols-2 gap-2">
          <input value={block.text} onChange={(e) => set({ text: e.target.value })} className={inputCls} placeholder="Button text" />
          <input value={block.url} onChange={(e) => set({ url: e.target.value })} className={inputCls} placeholder="Destination URL" />
          {block.type === "affiliate" && <input value={block.disclosure || ""} onChange={(e) => set({ disclosure: e.target.value })} className={`${inputCls} col-span-2`} placeholder="Affiliate disclosure" />}
        </div>
      )}

      {block.type === "product" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input value={block.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} placeholder="Tool name" />
            <input value={block.url} onChange={(e) => set({ url: e.target.value })} className={inputCls} placeholder="URL" />
          </div>
          <input value={block.description} onChange={(e) => set({ description: e.target.value })} className={inputCls} placeholder="Short description" />
          <ImageField value={block.image} onChange={(image) => set({ image })} label="Logo / image" />
        </div>
      )}

      {block.type === "html" && <textarea value={block.html} onChange={(e) => set({ html: e.target.value })} rows={4} className={`${inputCls} font-mono text-xs`} placeholder="Raw HTML / embed code" />}

      {block.type === "divider" && <div className="text-xs text-slate-500 text-center py-2">Horizontal divider</div>}
    </div>
  );
}

export default function BlockEditor({ blocks, onChange }) {
  const [adding, setAdding] = useState(false);

  const update = (i, b) => { const next = [...blocks]; next[i] = b; onChange(next); };
  const remove = (i) => onChange(blocks.filter((_, x) => x !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = (type) => { onChange([...blocks, newBlock(type)]); setAdding(false); };

  return (
    <div className="space-y-3" data-testid="block-editor">
      {blocks.map((b, i) => (
        <BlockCard key={b.id || i} block={b} onChange={(nb) => update(i, nb)} onDelete={() => remove(i)} onMove={(d) => move(i, d)} first={i === 0} last={i === blocks.length - 1} />
      ))}

      {adding ? (
        <div className="rounded-xl border border-brand/40 bg-ink-900 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-white">Add a block</span>
            <button type="button" onClick={() => setAdding(false)} className="text-slate-500 hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {BLOCK_TYPES.map((b) => (
              <button key={b.type} type="button" onClick={() => add(b.type)} data-testid={`add-block-${b.type}`} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-ink-600 text-slate-300 hover:border-brand hover:text-brand transition-colors text-sm">
                <b.icon size={15} /> {b.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} data-testid="add-block-toggle" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-ink-600 text-slate-400 hover:border-brand hover:text-brand transition-colors text-sm font-semibold">
          <Plus size={16} /> Add block
        </button>
      )}
    </div>
  );
}
